import {Dispatch, SetStateAction, useEffect, useState} from 'react'

import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'

import {useMeStore} from '@progyny/store/meStore'

dayjs.extend(localizedFormat)

type Locales = {
  [key: string]: () => Promise<any>
}
type Map = {
  [key: string]: string
}

export const DayjsLocales = [
  'be-NL',
  'de',
  'de-at',
  'de-ch',
  'en-gb',
  'en',
  'nl',
]
export const DEFAULT_DAYJS_LOCALE = 'en-gb'
export const CountryLocaleMap: Map = {
  at: 'de-at',
  be: 'be-NL',
  ch: 'de-ch',
  de: 'de',
  gb: 'en-uk',
  nl: 'nl',
  us: 'en',
}
export const repickLocale = (country: string) => {
  return CountryLocaleMap[country]
}

// https://stackoverflow.com/a/66060403
const locales: Locales = {
  de: () => import('dayjs/locale/de'),
  'de-de': () => import('dayjs/locale/de'),
  'de-at': () => import('dayjs/locale/de-at'),
  'de-ch': () => import('dayjs/locale/de-ch'),
  en: () => import('dayjs/locale/en'),
  'en-gb': () => import('dayjs/locale/en-gb'),
  'en-us': () => import('dayjs/locale/en'),
  'be-NL': () => import('dayjs/locale/nl-be'),
  nl: () => import('dayjs/locale/nl'),
}

function formatDate(
  locale: string,
  value: string,
  setter: Dispatch<SetStateAction<string | null | undefined>>
) {
  locales[locale]().then(() => {
    try {
      const parsed = dayjs(value)
      // WARNING: dayjs will silently default to 'en' if provided with a
      // bad locale.
      setter(parsed.locale(locale).format('DD.MM.YYYY'))
    } catch (error) {
      setter(value)
    }
  })
}

function resolveLocale(languageCode: string) {
  /*
   This favors the country over language, as one is more used to formatting of
   the country than language. This is especially true for English, since the
   default locale for English is in fact en-US, which is the only country in
   the world that puts the month before the day.
  */
  const parts = languageCode.split('-')
  let selectedLocale = languageCode

  if (!DayjsLocales.includes(languageCode)) {
    const repicked = repickLocale(parts[1])
    if (DayjsLocales.includes(repicked)) {
      selectedLocale = repicked
    } else if (DayjsLocales.includes(parts[0])) {
      selectedLocale = parts[0]
    } else {
      selectedLocale = DEFAULT_DAYJS_LOCALE
    }
  }
  return selectedLocale
}
const useLocalizedDate = (value: string | null | undefined) => {
  const [localized, setLocalized] = useState<string | null | undefined>(null)
  const getMyLocale = useMeStore((s) => s.getMyLocale)
  const languageCode = getMyLocale().toLowerCase()
  const selectedLocale = resolveLocale(languageCode)

  useEffect(() => {
    if (value) {
      formatDate(selectedLocale, value, setLocalized)
    }
  }, [setLocalized, value, selectedLocale])
  return localized
}

export default useLocalizedDate
